import { useCallback, useEffect, useMemo, useState } from 'react';
import { ActorType } from 'features/new-actor/enums/actorType';
import { FormConfig } from 'types/form/formConfig';
import { BorrowerInsuredFormValues } from './borrowerInsuredForm';

export type BorrowerActorType =
  | ActorType.BorrowerNatural
  | ActorType.BorrowerLegal;

export interface BorrowerFormConfig {
  [ActorType.BorrowerNatural]: FormConfig<any>;
  [ActorType.BorrowerLegal]: FormConfig<any>;
}

export type BorrowerFormInitialData = Partial<
  Record<BorrowerActorType, Partial<BorrowerInsuredFormValues>>
>;

export const useBorrowerActorType = (
  formConfig: BorrowerFormConfig,
  initialData?: BorrowerFormInitialData
) => {
  const firstActorType = useMemo(
    () => Object.keys(formConfig)[0] as BorrowerActorType,
    [formConfig]
  );
  const [actorType, setActorType] = useState<BorrowerActorType>(firstActorType);

  const initialValues = useMemo(
    () =>
      Object.entries(formConfig).reduce(
        (acc, [key, value]) => {
          acc[key as BorrowerActorType] = value.initialValues;
          return acc;
        },
        {} as Partial<Record<BorrowerActorType, BorrowerInsuredFormValues>>
      ),
    [formConfig]
  );

  const [savedValues, setSavedValues] = useState<
    Partial<Record<BorrowerActorType, BorrowerInsuredFormValues>>
  >(initialValues);

  const [currentFormConfig, setCurrentFormConfig] = useState(
    formConfig[actorType]
  );

  // Update savedValues when initialData changes (e.g., when selecting a person from search)
  useEffect(() => {
    if (initialData) {
      const newSavedValues = Object.keys(initialData).reduce(
        (acc, key) => {
          const actorKey = key as BorrowerActorType;
          if (actorKey in formConfig) {
            setActorType(actorKey);
            acc[actorKey] = {
              ...formConfig[actorKey].initialValues,
              ...initialData[actorKey]
            } as BorrowerInsuredFormValues;
          }
          return acc;
        },
        { ...initialValues } as Partial<
          Record<BorrowerActorType, BorrowerInsuredFormValues>
        >
      );
      setSavedValues(newSavedValues);
    } else {
      setSavedValues(initialValues);
    }
  }, [initialData, formConfig, initialValues]);

  useEffect(() => {
    setCurrentFormConfig(formConfig[actorType]);
  }, [actorType, formConfig]);

  const handleActorTypeChange = useCallback(
    (
      newType: BorrowerActorType,
      currentValues: BorrowerInsuredFormValues,
      setCurrentValues: (values: BorrowerInsuredFormValues) => void
    ) => {
      setSavedValues((prev) => ({ ...prev, [actorType]: currentValues }));
      setActorType(newType);
      setCurrentValues(savedValues[newType] as BorrowerInsuredFormValues);
    },
    [actorType, savedValues]
  );

  return { actorType, handleActorTypeChange, currentFormConfig, savedValues };
};
